import React, { createContext, useContext, useState, useCallback } from "react";
import axios from "axios";
import es from "../translations/es";
import en from "../translations/en";
import pt from "../translations/pt";

const translations = { es, en, pt };
const SUPPORTED = ["es", "en", "pt"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Resolves the initial language in priority order:
 * 1. User-saved preference in localStorage (manual selection via the language switcher)
 * 2. Spanish as the default language for InfoCityJobs on first visit
 *
 * Note: automatic browser-language detection was removed — it caused visitors
 * with an "en" browser locale to see English on first load even though the
 * portal's primary market/default language is Spanish. Users can still switch
 * to English or Portuguese manually via the language switcher (Priority 1).
 */
function resolveInitialLanguage() {
  // Priority 1: Respect any previously saved manual selection
  const saved = localStorage.getItem("infocity_language");
  if (saved && SUPPORTED.includes(saved)) {
    return saved;
  }

  // Priority 2: Default to Spanish
  return "es";
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(resolveInitialLanguage);

  /**
   * Manual language change — always persists and wins over auto-detect.
   * Once the user selects a language, resolveInitialLanguage() will
   * return it on every subsequent visit (Priority 1 above).
   *
   * `persist: false` is used ONLY when AuthContext is syncing the selector
   * FROM the backend's preferred_language (login/checkAuth) — in that case
   * we must not PATCH the exact same value straight back to the server.
   */
  const changeLanguage = useCallback((lang, { persist = true } = {}) => {
    if (!translations[lang]) return;
    setLanguage(lang);
    localStorage.setItem("infocity_language", lang);
    if (persist) {
      const token = localStorage.getItem("auth_token");
      if (token) {
        axios
          .put(`${API}/users/me/language`, { preferred_language: lang }, { headers: { Authorization: `Bearer ${token}` } })
          .catch(() => {});
      }
    }
  }, []);

  // t("nav.home") → resolves nested key "nav" → "home"
  const t = useCallback(
    (key, fallback = key) => {
      const parts = key.split(".");
      let value = translations[language];
      for (const part of parts) {
        if (value === null || value === undefined) return fallback;
        value = value[part];
      }
      return typeof value === "string" ? value : fallback;
    },
    [language]
  );

  // tList("ai.suggestions") → resolves a translated ARRAY (t() only returns strings)
  const tList = useCallback(
    (key, fallback = []) => {
      const parts = key.split(".");
      let value = translations[language];
      for (const part of parts) {
        if (value === null || value === undefined) return fallback;
        value = value[part];
      }
      return Array.isArray(value) ? value : fallback;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t, tList }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside LanguageProvider");
  return ctx;
}

